import { useOrganization } from '@clerk/clerk-react';
import { OrganizationType } from 'modules/Common/utils/Utility';

/**
 * The signed-in organisation's type, or `null` before Clerk has resolved it.
 *
 * The null matters: it is not "not a declarant", it is "not known yet", and a
 * caller that branches on org type has to decide what to do in the gap rather
 * than fall into whichever branch `undefined` happens to take.
 *
 * Same read `PrivateRoute` uses for `allowedTypes`.
 */
export const useOrgType = () => {
  const { organization } = useOrganization();
  return organization?.publicMetadata?.type ?? null;
};

/**
 * Consultants get the full read/compare experience but none of the write
 * actions: commit, data requests, and entity creation are declarant-only.
 *
 * Lives in Common because two features now ask the question — the Quoting Map
 * gates every write on it, and Cost Radar decides which mode it can even
 * offer.
 */
export const useIsDeclarant = () => useOrgType() === OrganizationType.Declarant;

/**
 * Whether Clerk has resolved the organisation yet — i.e. whether `useOrgType`'s
 * `null` means "no type" or "not known yet".
 *
 * Callers that GATE A QUERY on the org type need this: while it is false the
 * query has not run and its `isLoading` is `false`, so an empty result reads as
 * "nothing to show" when it actually means "nothing asked yet". That gap is how
 * the map flashed "No installations match CN x" over a page that was still
 * booting.
 */
export const useOrgTypeResolved = () => useOrganization().isLoaded;
